import React, { useEffect, useState } from 'react';
import { apiUrl } from './config/api';
import { authFetch, storeSessionToken, MIN_PASSWORD_LENGTH } from './config/auth';
import useFocusTrap from './useFocusTrap';
import AvButton from './design/nedi/AvButton';
import AvField from './design/nedi/AvField';
import { NediPanel, NediScreen } from './design/nedi/NediLayout';
import { mountNediTheme } from './design/nedi/mountNediTheme';
import './design/atlasVivo/tokens.css';
import './design/atlasVivo/Buttons.css';
import './LoginModal.css';
import { t } from './i18n';

const CRAFTIA_SSO_URL = 'https://craftia.nedisonn.cc/auth/sso';

const getResetTokenFromUrl = () => {
  if (typeof window === 'undefined') return '';
  return new URLSearchParams(window.location.search).get('resetToken') || '';
};

const getSsoTokenFromUrl = () => {
  if (typeof window === 'undefined') return '';
  return new URLSearchParams(window.location.search).get('sso_token') || '';
};

const clearAuthQueryParamsFromUrl = () => {
  if (typeof window === 'undefined' || !window.history?.replaceState) return;
  const url = new URL(window.location.href);
  url.searchParams.delete('resetToken');
  url.searchParams.delete('sso_token');
  window.history.replaceState({}, '', url.toString());
};

const clearResetTokenFromUrl = () => {
  if (typeof window === 'undefined' || !window.history?.replaceState) return;
  const url = new URL(window.location.href);
  url.searchParams.delete('resetToken');
  window.history.replaceState({}, '', url.toString());
};

const modeTitle = (mode) => {
  if (mode === 'forgot') return t('web.login.forgotTitle');
  if (mode === 'reset') return t('web.login.resetTitle');
  return t('web.login.title');
};

function LoginModal({ onLoginSuccess }) {
  const initialResetToken = getResetTokenFromUrl();
  const [mode, setMode] = useState(initialResetToken ? 'reset' : 'login');
  const [resetToken] = useState(initialResetToken);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [ssoBootstrapping, setSsoBootstrapping] = useState(() => Boolean(getSsoTokenFromUrl()));
  const containerRef = useFocusTrap(true);
  const isLogin = mode === 'login';

  useEffect(() => {
    mountNediTheme();
  }, []);

  const resetMessages = () => {
    setErrorMessage('');
    setSuccessMessage('');
  };

  const switchMode = (nextMode) => {
    setMode(nextMode);
    resetMessages();
  };

  const backToLogin = (message) => {
    clearResetTokenFromUrl();
    setMode('login');
    setErrorMessage('');
    setSuccessMessage(message || '');
    setNewPassword('');
    setConfirmPassword('');
  };

  const connectWithCraftia = () => {
    const redirectUri = window.location.origin + window.location.pathname;
    window.location.href =
      `${CRAFTIA_SSO_URL}?redirect_uri=${encodeURIComponent(redirectUri)}&app=Timeline`;
  };

  useEffect(() => {
    const ssoToken = getSsoTokenFromUrl();
    if (!ssoToken) return undefined;

    let cancelled = false;
    (async () => {
      setSsoBootstrapping(true);
      setErrorMessage('');
      try {
        const response = await authFetch(apiUrl('/api/auth/craftia-sso'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sso_token: ssoToken }),
        });
        const data = await response.json();
        if (cancelled) return;
        if (response.ok) {
          storeSessionToken(data.token);
          clearAuthQueryParamsFromUrl();
          onLoginSuccess(data.user);
          return;
        }
        setErrorMessage(data.error || t('web.login.error.craftia'));
      } catch {
        if (!cancelled) {
          setErrorMessage(t('web.login.error.craftiaServer'));
        }
      } finally {
        if (!cancelled) setSsoBootstrapping(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [onLoginSuccess]);

  const handleLoginOrRegisterSubmit = async (e) => {
    e.preventDefault();
    resetMessages();

    if (!username.trim() || !password) {
      setErrorMessage(t('web.login.error.fillAll'));
      return;
    }
    if (!isLogin && password.length < MIN_PASSWORD_LENGTH) {
      setErrorMessage(t('web.login.error.passwordMin', { n: MIN_PASSWORD_LENGTH }));
      return;
    }

    setLoading(true);
    const endpoint = isLogin ? '/api/auth/login' : '/api/auth/register';
    try {
      const response = await authFetch(apiUrl(endpoint), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      const data = await response.json();

      if (response.ok) {
        if (isLogin) {
          storeSessionToken(data.token);
          onLoginSuccess(data.user);
        } else {
          setSuccessMessage(data.message + t('web.login.success.register'));
          setMode('login');
          setPassword('');
        }
      } else {
        setErrorMessage(data.error || t('web.login.error.generic'));
      }
    } catch {
      setErrorMessage(t('web.login.error.server'));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    resetMessages();

    if (!email.trim()) {
      setErrorMessage(t('web.login.error.emailRequired'));
      return;
    }

    setLoading(true);
    try {
      const response = await authFetch(apiUrl('/api/auth/forgot-password'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await response.json();
      if (response.ok) {
        setSuccessMessage(data.message);
      } else {
        setErrorMessage(data.error || t('web.login.error.generic'));
      }
    } catch {
      setErrorMessage(t('web.login.error.server'));
    } finally {
      setLoading(false);
    }
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    resetMessages();

    if (!newPassword || !confirmPassword) {
      setErrorMessage(t('web.login.error.resetFields'));
      return;
    }
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setErrorMessage(t('web.login.error.passwordMin', { n: MIN_PASSWORD_LENGTH }));
      return;
    }
    if (newPassword !== confirmPassword) {
      setErrorMessage(t('web.login.error.resetMismatch'));
      return;
    }

    setLoading(true);
    try {
      const response = await authFetch(apiUrl('/api/auth/reset-password'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: resetToken, new_password: newPassword }),
      });
      const data = await response.json();
      if (response.ok) {
        backToLogin(data.message || t('web.login.success.reset'));
      } else {
        setErrorMessage(data.error || t('web.login.error.resetFailed'));
      }
    } catch {
      setErrorMessage(t('web.login.error.server'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-modal-overlay">
      {/* Package CEs: nedi-screen + nedi-panel (5.2 Timeline pilot; mirrors FinZen AuthShell). */}
      <NediScreen className="login-nedi-screen">
        <div
          className="login-dialog"
          ref={containerRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="login-modal-title"
          tabIndex={-1}
        >
          <header className="login-header">
            <span className="login-logo">Timeline</span>
            <h1 id="login-modal-title">{modeTitle(mode)}</h1>
          </header>

          <NediPanel className="login-nedi-panel">
            {(mode === 'login' || mode === 'register') && (
              <>
                {isLogin && (
                  <div className="login-craftia-block">
                    <AvButton
                      variant="primary"
                      className="login-craftia-btn"
                      onClick={connectWithCraftia}
                      disabled={loading || ssoBootstrapping}
                      block
                    >
                      {ssoBootstrapping
                        ? t('web.login.craftia.loading')
                        : t('web.login.craftia')}
                    </AvButton>
                    <p className="login-info-text">
                      {t('web.login.craftia.hint')}
                    </p>
                    <div className="login-divider" role="separator">
                      <span>{t('web.login.orLocal')}</span>
                    </div>
                  </div>
                )}

                <div className="login-tabs">
                  <button
                    type="button"
                    className={`login-tab-btn ${isLogin ? 'active' : ''}`}
                    onClick={() => switchMode('login')}
                  >
                    {t('web.login.tab.signIn')}
                  </button>
                  <button
                    type="button"
                    className={`login-tab-btn ${!isLogin ? 'active' : ''}`}
                    onClick={() => switchMode('register')}
                  >
                    {t('web.login.tab.register')}
                  </button>
                </div>

                <form onSubmit={handleLoginOrRegisterSubmit} className="login-form">
                  <AvField
                    name="username-input"
                    label={t('web.login.username')}
                    type="text"
                    value={username}
                    onChange={setUsername}
                    placeholder={t('web.login.username.ph')}
                    autoComplete="username"
                    required
                  />

                  <AvField
                    name="password-input"
                    label={t('web.login.password')}
                    type="password"
                    value={password}
                    onChange={setPassword}
                    placeholder={isLogin ? t('web.login.password.ph') : t('web.login.password.min', { n: MIN_PASSWORD_LENGTH })}
                    autoComplete={isLogin ? 'current-password' : 'new-password'}
                    minLength={isLogin ? undefined : MIN_PASSWORD_LENGTH}
                    required
                  />

                  {isLogin && (
                    <button
                      type="button"
                      className="login-link-btn"
                      onClick={() => switchMode('forgot')}
                    >
                      {t('web.login.forgotLink')}
                    </button>
                  )}

                  {!isLogin && (
                    <p className="login-info-text">
                      {t('web.login.register.note')}
                    </p>
                  )}

                  {errorMessage && (
                    <div className="login-error-msg" role="alert">{errorMessage}</div>
                  )}
                  {successMessage && (
                    <div className="login-success-msg" role="status">{successMessage}</div>
                  )}

                  <AvButton
                    type="button"
                    variant="primary"
                    disabled={loading}
                    loading={loading}
                    block
                    onClick={(e) => {
                      const form = e.currentTarget.closest('form');
                      if (form?.requestSubmit) form.requestSubmit();
                      else form?.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
                    }}
                  >
                    {loading ? t('web.login.submit.loading') : isLogin ? t('web.login.submit.signIn') : t('web.login.submit.register')}
                  </AvButton>
                </form>
              </>
            )}

            {mode === 'forgot' && (
              <form onSubmit={handleForgotSubmit} className="login-form">
                <p className="login-info-text">
                  {t('web.login.forgot.help')}
                </p>
                <AvField
                  name="forgot-email-input"
                  label={t('web.login.email')}
                  type="email"
                  value={email}
                  onChange={setEmail}
                  placeholder="seu@email.com"
                  autoComplete="email"
                  required
                />

                {errorMessage && (
                  <div className="login-error-msg" role="alert">{errorMessage}</div>
                )}
                {successMessage && (
                  <div className="login-success-msg" role="status">{successMessage}</div>
                )}

                <AvButton
                  type="button"
                  variant="primary"
                  disabled={loading}
                  loading={loading}
                  block
                  onClick={(e) => {
                    const form = e.currentTarget.closest('form');
                    if (form?.requestSubmit) form.requestSubmit();
                    else form?.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
                  }}
                >
                  {loading ? t('web.login.forgot.sending') : t('web.login.forgot.send')}
                </AvButton>
                <button
                  type="button"
                  className="login-link-btn"
                  onClick={() => switchMode('login')}
                >
                  {t('web.login.back')}
                </button>
              </form>
            )}

            {mode === 'reset' && (
              <form onSubmit={handleResetSubmit} className="login-form">
                <AvField
                  name="reset-new-password-input"
                  label={t('web.login.reset.new')}
                  type="password"
                  value={newPassword}
                  onChange={setNewPassword}
                  autoComplete="new-password"
                  minLength={MIN_PASSWORD_LENGTH}
                  required
                />
                <AvField
                  name="reset-confirm-password-input"
                  label={t('web.login.reset.confirm')}
                  type="password"
                  value={confirmPassword}
                  onChange={setConfirmPassword}
                  autoComplete="new-password"
                  minLength={MIN_PASSWORD_LENGTH}
                  required
                />

                {errorMessage && (
                  <div className="login-error-msg" role="alert">{errorMessage}</div>
                )}
                {successMessage && (
                  <div className="login-success-msg" role="status">{successMessage}</div>
                )}

                <AvButton
                  type="button"
                  variant="primary"
                  disabled={loading}
                  loading={loading}
                  block
                  onClick={(e) => {
                    const form = e.currentTarget.closest('form');
                    if (form?.requestSubmit) form.requestSubmit();
                    else form?.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
                  }}
                >
                  {loading ? t('web.login.reset.saving') : t('web.login.reset.save')}
                </AvButton>
                <button
                  type="button"
                  className="login-link-btn"
                  onClick={() => backToLogin()}
                >
                  {t('web.login.back')}
                </button>
              </form>
            )}
          </NediPanel>
        </div>
      </NediScreen>
    </div>
  );
}

export default LoginModal;
